// src/components/auth/LoginForm.jsx
import React, { useState } from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { User, Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { signInErrorMessage } from '../../lib/accessPolicy';

export default function LoginForm() {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [error, setError] = useState('');

  const { login, register, loginWithGoogle, isSupabaseEnabled, googleOnly } = useAuth();
  const showGoogle = isSupabaseEnabled || googleOnly;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData.email, formData.password, formData.name);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setFormData({ email: '', password: '', name: '' });
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      await loginWithGoogle();
    } catch (err) {
      setError(signInErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen">
      <main className="auth-panel">
        <div className="auth-brand">
          <img src="/spcc-logo.png" alt="" width="40" height="40" />
          <h1>{isLogin ? 'Sign in to SPCC' : 'Create your SPCC account'}</h1>
          <p>Strategic Product Command Center</p>
          {googleOnly && <p>Access is restricted to approved Google accounts.</p>}
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14 }}>
          {showGoogle && (
            <Button type="button" variant="secondary" size="lg" onClick={handleGoogleSignIn} disabled={loading} className="btn-block">
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          )}

          {showGoogle && !googleOnly && (
            <div className="auth-divider"><span>or</span></div>
          )}

          {!googleOnly && !isLogin && (
            <div>
              <Label htmlFor="name">Full name</Label>
              <div className="input-container">
                <Input
                  id="name"
                  type="text"
                  autoComplete="name"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
                <User size={15} className="input-icon-left" />
              </div>
            </div>
          )}

          {!googleOnly && <>
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="input-container">
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
                <Mail size={15} className="input-icon-left" />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="input-container has-both-icons">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  required
                />
                <Lock size={15} className="input-icon-left" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="input-icon-right"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{ background: 'none', border: 'none', padding: 0, display: 'inline-flex' }}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          </>}

          {error && <div className="v2-error" role="alert">{error}</div>}

          {!googleOnly && <>
            <Button type="submit" variant="primary" size="lg" disabled={loading} className="btn-block">
              {loading ? 'Please wait…' : (isLogin ? 'Sign In to SPCC' : 'Create SPCC Account')}
            </Button>

            <p style={{ margin: 0, textAlign: 'center', fontSize: 13, color: 'var(--fg-muted)' }}>
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button
                type="button"
                onClick={toggleMode}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent-fg)', fontWeight: 500, fontSize: 13 }}
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </>}
        </form>
      </main>
    </div>
  );
}
